import {
	memo,
	useEffect,
	useState,
} from 'react'
import moment from 'moment'
import pick from 'ramda/src/pick'
import isNil from 'ramda/src/isNil'
import length from 'ramda/src/length'
import dropCardConnector from 'root/src/client/web/list/listItemComponents/connectors/dropCardConnector'
import withModuleContext from 'root/src/client/util/withModuleContext'
import { DareCard } from 'root/src/client/web/list/listItemComponents/dare/DareCard'
import TooltipWrapper from 'root/src/client/web/base/TooltipWraper'
import {
	DARE_SUBMISSION_MODAL_MODULE_ID,
	GET_STARTED_MODULE_ID,
} from 'root/src/shared/descriptions/modules/moduleIds'
import { sentryClientObjMessage } from 'root/src/client/util/sentry'
import getBountyName from 'root/src/shared/util/getBountyName'
import { comingSoonMaskText } from 'root/src/shared/constants/dropEvent'
import { projectAcceptedKey } from 'root/src/shared/constants/keys'
import { useLocalStorage } from 'root/src/client/web/hooks/useLocalStorage'
import { DareContextProvider } from 'root/src/client/web/list/listItemComponents/dare/contexts/dare'
import providers from 'root/src/shared/constants/providers'
import { useDareAssignee } from 'root/src/client/logic/project/hooks/useDareAssignee'

const DareCardDrop = ({
	dare, isAuthenticated, campaignId, canAccept, submitting, setSubmitting,
	acceptProjectFromDare, platformId, platform, setInput, dropEvent, recordId,
	mnemonicId, redirectToCreateBrandProjectForm, displayModal, comingSoon,
	toggleEmailSubscription, emailSubscription, canAccessCreateBrandProject,
	userData, userId, showTermsModal, pointsToEarn, canEarnPoints,
	tokens, creatorCampaignPointsLimit, userCampaignData, getUserDropEventLandingShortUrl,
	followerCountByPlatforms, displayHowToEarnPointsModal, fetchUserCampaignData, tooltipClassName, ...props
}) => {
	const [error, setError] = useState('')

	const [showSubmissionModal] = useLocalStorage('showSubmissionModal', true)

	const {
		initialProduct,
		campaignName,
		mnemonicId: campaignMnemonicId,
		cta,
		url,
	} = dropEvent

	const assignee = useDareAssignee({ dare })

	const acceptDareFn = async () => acceptProjectFromDare({
		paymentInfo: dare.paymentInfo,
		dropDareId: dare.id,
		dare,
		assignees: [assignee],
		dropEvent,
	},
	{
		initialProduct,
		cta,
		url,
		...pick(['type', 'level', 'product', 'amount', 'games'], dare),
	})

	const acceptDare = async () => {
		try {
			if (showTermsModal) {
				displayHowToEarnPointsModal({
					onSubmit: async () => {
						await acceptProjectFromDare({
							paymentInfo: dare.paymentInfo,
							dropDareId: dare.id,
							dare,
							assignees: [assignee],
							dropEvent,
						})
					},
				})
				return
			}

			if (dare.platform !== providers.any && showSubmissionModal) {
				await displayModal(DARE_SUBMISSION_MODAL_MODULE_ID, {
					onCancel: () => displayModal(undefined),
					onAccept: () => {
						// !!!! This displayModal(undefined) is important.
						// !!!! Otherwise, we're not closing modal properly, leading to creating duplicate dares.
						displayModal(undefined)
						acceptDareFn()
					},
				})
			} else {
				await acceptDareFn()
			}
		} catch (err) {
			if (isNil(userData?.creatorData?.ccv)) {
				sentryClientObjMessage(`User can't accept dare from "${campaignMnemonicId}" - ${moment.utc().format()}`, {
					campaignId,
					userId,
					isAuthenticated,
					platformId,
					platform,
					mnemonicId,
					...userData,
					error: err,
				})
				setError('Your data hasn\'t loaded yet. Please reload and try again.')
			} else {
				setError(err.error)
			}
		}
	}

	const onClick = async (setHover) => {
		if (submitting || dare.userCreated) {
			return
		}

		if (!isAuthenticated) {
			displayModal(GET_STARTED_MODULE_ID)
			return
		}

		if (comingSoon) {
			if (!emailSubscription) {
				toggleEmailSubscription(setError)
			}
			return
		}

		if (canAccessCreateBrandProject) {
			redirectToCreateBrandProjectForm({
				...pick(['id', 'title', 'description', 'paymentInfo'], dare),
				campaign: { name: campaignName, campaignId },
			})
			return
		}

		if (!canAccept) {
			return
		}

		setHover(true)
		setSubmitting(true)
		try {
			await acceptDare()
			await fetchUserCampaignData(dropEvent.mnemonicId)
		} catch (err) {
			console.error(err)
		}
		setSubmitting(false)
		setHover(false)
	}

	useEffect(() => {
		let timeout
		if (error) {
			timeout = setTimeout(() => {
				setError('')
			}, 5000)
		}
		return () => {
			clearTimeout(timeout)
		}
	}, [error])

	const { product } = dare
	const { name, type, platform: productPlatform, region } = product
	const brandProductName = comingSoon
		? comingSoonMaskText
		: getBountyName({
			name,
			type,
			comingSoon,
			region,
			platform: productPlatform,
		}, true)
	return (
		<TooltipWrapper tooltipText={error} className={tooltipClassName} visible>
			<DareContextProvider value={{
				comingSoon,
				emailSubscription,
				onClick,
				campaignId,
				brandProduct: { brandProductName, type },
				projectAccepted: dare.status === projectAcceptedKey,
				projectCompleted: length(dare.deliveries) > 0,
				projectDeliveryPending: dare.isDeliveryPending,
				projectStatus: dare.status,
				pointsToEarn,
				canEarnPoints,
				tokens,
				darePlatform: dare.platform,
				brand: dropEvent.brand,
				canAccessCreateBrandProject,
				dare,
				...props,
			}}
			>
				<DareCard />
			</DareContextProvider>
		</TooltipWrapper>
	)
}

export default withModuleContext(
	dropCardConnector(memo(DareCardDrop)),
)
